"use client";

import React, {
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
} from "react";
import { gsap } from "gsap";
import { CustomEase } from "gsap/CustomEase";

gsap.registerPlugin(CustomEase);
CustomEase.create("hop", "0.9, 0, 0.1, 1");

interface GalleryImage {
  src: string;
  alt: string;
  title: string;
  number: string;
}

interface ItemSize {
  width: number;
  height: number;
}

interface GridPosition {
  x: number;
  y: number;
}

// Removed unused VisibleItem interface

const InfiniteDraggableGallery = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  // State management
  const [canDrag] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  // Position and drag state
  const targetPosition = useRef({ x: 0, y: 0 });
  const currentPosition = useRef({ x: 0, y: 0 });
  const dragVelocity = useRef({ x: 0, y: 0 });
  const lastDragTime = useRef(0);
  const startPosition = useRef({ x: 0, y: 0 });
  const visibleItems = useRef(new Set<string>());
  const lastUpdateTime = useRef(0);

  // Settings
  const settings = {
    baseWidth: 200,
    smallHeight: 400,
    largeHeight: 400,
    itemGap: 8,
    hoverScale: 1.0,
    dragEase: 0.075,
    momentumFactor: 200,
    bufferZone: 4,
    borderRadius: 8,
    vignetteSize: 0,
  };

  // Enhanced image data with titles and metadata
  const images: GalleryImage[] = useMemo(
    () => [
      {
        src: "/images/barista/gallery/Simulator Screenshot - iPhone 16 Pro - 2025-08-15 at 23.16.49.png",
        alt: "Barista App Home Screen",
        title: "Home Screen",
        number: "#00001",
      },
      {
        src: "/images/barista/gallery/Simulator Screenshot - iPhone 16 Pro - 2025-08-15 at 23.17.25.png",
        alt: "Barista App Coffee Selection",
        title: "Coffee Selection",
        number: "#00002",
      },
      {
        src: "/images/barista/gallery/Simulator Screenshot - iPhone 16 Pro - 2025-08-15 at 23.17.30.png",
        alt: "Barista App Brewing Process",
        title: "Brewing Process",
        number: "#00003",
      },
      {
        src: "/images/barista/gallery/Simulator Screenshot - iPhone 16 Pro - 2025-08-15 at 23.17.39.png",
        alt: "Barista App Recipe Details",
        title: "Recipe Details",
        number: "#00004",
      },
      {
        src: "/images/barista/gallery/Simulator Screenshot - iPhone 16 Pro - 2025-08-15 at 23.17.51.png",
        alt: "Barista App Timer Interface",
        title: "Timer Interface",
        number: "#00005",
      },
      {
        src: "/images/barista/gallery/Simulator Screenshot - iPhone 16 Pro - 2025-08-15 at 23.18.01.png",
        alt: "Barista App Settings",
        title: "Settings",
        number: "#00006",
      },
      {
        src: "/images/barista/gallery/Simulator Screenshot - iPhone 16 Pro - 2025-08-15 at 23.18.10.png",
        alt: "Barista App Profile",
        title: "Profile",
        number: "#00007",
      },
    ],
    []
  );

  // Define item sizes based on settings
  const itemSizes: ItemSize[] = useMemo(
    () => [
      {
        width: settings.baseWidth,
        height: settings.smallHeight,
      },
      {
        width: settings.baseWidth,
        height: settings.largeHeight,
      },
    ],
    [settings.baseWidth, settings.smallHeight, settings.largeHeight]
  );

  const columns = 4;
  const cellWidth = settings.baseWidth + settings.itemGap;
  const cellHeight =
    Math.max(settings.smallHeight, settings.largeHeight) + settings.itemGap;

  // Get item size based on position
  const getItemSize = useCallback(
    (row: number, col: number): ItemSize => {
      const sizeIndex = Math.abs((row * columns + col) % itemSizes.length);
      return itemSizes[sizeIndex];
    },
    [columns, itemSizes]
  );

  // Generate unique ID for grid position
  const getItemId = useCallback((col: number, row: number): string => {
    return `${col},${row}`;
  }, []);

  // Get absolute position for an item
  const getItemPosition = useCallback(
    (col: number, row: number): GridPosition => {
      return {
        x: col * cellWidth,
        y: row * cellHeight,
      };
    },
    [cellWidth, cellHeight]
  );

  // Update visible items based on viewport
  const updateVisibleItems = useCallback(() => {
    if (!containerRef.current || !canvasRef.current) return;

    const buffer = settings.bufferZone;
    const viewWidth = window.innerWidth * (1 + buffer);
    const viewHeight = window.innerHeight * (1 + buffer);
    const currentX = currentPosition.current.x;
    const currentY = currentPosition.current.y;

    // Calculate visible range
    const startCol = Math.floor((-currentX - viewWidth / 2) / cellWidth);
    const endCol = Math.ceil((-currentX + viewWidth * 1.5) / cellWidth);
    const startRow = Math.floor((-currentY - viewHeight / 2) / cellHeight);
    const endRow = Math.ceil((-currentY + viewHeight * 1.5) / cellHeight);

    const currentItems = new Set<string>();
    const canvas = canvasRef.current;

    // Create or update visible items
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const itemId = getItemId(col, row);
        currentItems.add(itemId);

        if (visibleItems.current.has(itemId)) continue;

        // Get size and position for this item
        const itemSize = getItemSize(row, col);
        const position = getItemPosition(col, row);

        // Calculate consistent item number for this position
        const itemNum = Math.abs((row * columns + col) % images.length);
        const imageData = images[itemNum];

        // Create the item element
        const item = document.createElement("div");
        item.className = "gallery-item";
        item.id = itemId;
        item.style.position = "absolute";
        item.style.width = `${itemSize.width}px`;
        item.style.height = `${itemSize.height}px`;
        item.style.left = `${position.x}px`;
        item.style.top = `${position.y}px`;
        item.style.borderRadius = "8px";
        item.style.overflow = "hidden";
        item.style.cursor = "grab";
        item.style.transition = "transform 0.3s ease, box-shadow 0.3s ease";

        // Store data attributes
        item.dataset.col = col.toString();
        item.dataset.row = row.toString();
        item.dataset.width = itemSize.width.toString();
        item.dataset.height = itemSize.height.toString();
        item.dataset.imageIndex = itemNum.toString();

        // Create image container
        const imageContainer = document.createElement("div");
        imageContainer.className = "item-image-container";
        imageContainer.style.width = "100%";
        imageContainer.style.height = "100%";
        imageContainer.style.position = "relative";
        imageContainer.style.overflow = "hidden";
        imageContainer.style.borderRadius = "8px";

        // Create image
        const img = document.createElement("img");
        img.src = imageData.src;
        img.alt = imageData.alt;
        img.style.width = "100%";
        img.style.height = "100%";
        img.style.objectFit = "contain";
        img.style.objectPosition = "top";
        img.style.transition = "transform 0.3s ease";
        img.style.borderRadius = "8px";
        img.draggable = false;

        imageContainer.appendChild(img);

        item.appendChild(imageContainer);

        // Add hover effects
        item.addEventListener("mouseenter", () => {
          if (canDrag) {
            gsap.to(item, {
              scale: settings.hoverScale,
              duration: 0.3,
              ease: "power2.out",
            });
            gsap.to(img, {
              scale: 1.1,
              duration: 0.3,
              ease: "power2.out",
            });
          }
        });

        item.addEventListener("mouseleave", () => {
          gsap.to(item, {
            scale: 1,
            duration: 0.3,
            ease: "power2.out",
          });
          gsap.to(img, {
            scale: 1,
            duration: 0.3,
            ease: "power2.out",
          });
        });

        canvas.appendChild(item);
        visibleItems.current.add(itemId);
      }
    }

    // Remove items that are no longer visible
    visibleItems.current.forEach((itemId) => {
      if (!currentItems.has(itemId)) {
        const item = document.getElementById(itemId);
        if (item && item.parentNode === canvas) {
          canvas.removeChild(item);
        }
        visibleItems.current.delete(itemId);
      }
    });
  }, [
    settings.bufferZone,
    cellWidth,
    cellHeight,
    getItemId,
    getItemSize,
    getItemPosition,
    columns,
    images,
    canDrag,
    settings.hoverScale,
  ]);

  // Animation loop
  useEffect(() => {
    let animationFrame: number;

    const animate = () => {
      if (canDrag && canvasRef.current) {
        const ease = settings.dragEase;
        const target = targetPosition.current;
        const current = currentPosition.current;

        current.x += (target.x - current.x) * ease;
        current.y += (target.y - current.y) * ease;

        canvasRef.current.style.transform = `translate(${current.x}px, ${current.y}px)`;

        const now = Date.now();
        if (now - lastUpdateTime.current > 120) {
          updateVisibleItems();
          lastUpdateTime.current = now;
        }
      }
      animationFrame = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [canDrag, settings.dragEase, updateVisibleItems]);

  // Mouse/touch event handlers
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (!canDrag) return;
      setIsDragging(true);
      startPosition.current = { x: e.clientX, y: e.clientY };
      container.style.cursor = "grabbing";
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !canDrag) return;

      const dx = e.clientX - startPosition.current.x;
      const dy = e.clientY - startPosition.current.y;

      const now = Date.now();
      const dt = Math.max(10, now - lastDragTime.current);
      lastDragTime.current = now;

      dragVelocity.current.x = dx / dt;
      dragVelocity.current.y = dy / dt;

      targetPosition.current.x += dx;
      targetPosition.current.y += dy;

      startPosition.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      if (!isDragging) return;
      setIsDragging(false);

      if (canDrag) {
        container.style.cursor = "grab";

        if (
          Math.abs(dragVelocity.current.x) > 0.1 ||
          Math.abs(dragVelocity.current.y) > 0.1
        ) {
          const momentumFactor = settings.momentumFactor;
          targetPosition.current.x += dragVelocity.current.x * momentumFactor;
          targetPosition.current.y += dragVelocity.current.y * momentumFactor;
        }
      }
    };

    // Touch event handlers
    const handleTouchStart = (e: TouchEvent) => {
      if (!canDrag) return;
      setIsDragging(true);
      startPosition.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging || !canDrag) return;

      const dx = e.touches[0].clientX - startPosition.current.x;
      const dy = e.touches[0].clientY - startPosition.current.y;

      targetPosition.current.x += dx;
      targetPosition.current.y += dy;

      startPosition.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    // Keyboard navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!canDrag) return;

      const moveDistance = 100;
      let moved = false;

      switch (e.key) {
        case "ArrowLeft":
          targetPosition.current.x += moveDistance;
          moved = true;
          break;
        case "ArrowRight":
          targetPosition.current.x -= moveDistance;
          moved = true;
          break;
        case "ArrowUp":
          targetPosition.current.y += moveDistance;
          moved = true;
          break;
        case "ArrowDown":
          targetPosition.current.y -= moveDistance;
          moved = true;
          break;
      }

      if (moved) {
        e.preventDefault();
      }
    };

    // Add event listeners
    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    container.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);

    window.addEventListener("keydown", handleKeyDown);

    // Set container properties
    container.style.cursor = "grab";
    container.setAttribute("tabindex", "0");
    container.setAttribute("role", "region");
    container.setAttribute(
      "aria-label",
      "Draggable image gallery. Use arrow keys or drag to navigate."
    );

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);

      container.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [canDrag, isDragging, settings.momentumFactor]);

  // Initial setup
  useEffect(() => {
    updateVisibleItems();
  }, [updateVisibleItems]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      updateVisibleItems();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateVisibleItems]);

  return (
    <section
      className="overflow-hidden relative"
      style={{ backgroundColor: "#2e2e2e" }}
    >
      {/* Page vignette effects */}
      <div
        className="fixed inset-0 pointer-events-none z-10"
        style={{
          background: "",
        }}
      />
      <div
        className="fixed inset-0 pointer-events-none z-10"
        style={{
          background: "",
        }}
      />
      <div
        className="fixed inset-0 pointer-events-none z-10"
        style={{
          background: "",
        }}
      />

      <div
        ref={containerRef}
        className="relative w-full h-[80vh] overflow-hidden z-30"
        style={{
          userSelect: "none",
          touchAction: "none",
        }}
      >
        <div
          ref={canvasRef}
          className="absolute inset-0"
          style={{
            width: "100%",
            height: "100%",
            willChange: "transform",
          }}
        />
      </div>
    </section>
  );
};

export default InfiniteDraggableGallery;
